import {
   Component,
   AfterViewInit,
   ViewChild,
   ElementRef,
   Input,
   OnInit,
} from '@angular/core';
import { Chart } from 'chart.js';
import { ChangeDetectorRef } from '@angular/core';
import { StockFilterService } from 'src/app/features/stock-screener/services/stock-filter.service';

@Component({
    selector: 'app-income-statement-annual-chart',
    templateUrl: './income-statement-annual-chart.component.html',
    styleUrls: ['./income-statement-annual-chart.component.scss'],
    standalone: false
})
export class IncomeStatementAnnualChartComponent
   implements OnInit, AfterViewInit {
   chart!: Chart;
   @Input() chartKey: string = '';
   @Input() specialArray: any[] = [];
   quarterlyData: any[] = [];
   yearlyData: any[] = [];
   setLoadingState: boolean = true;
   _isAnnual: boolean = true;
   annualData: any[] = [];

   period: number[] = [2020, 2021, 2022, 2023, 2024];
   page_num: number = 1;

   // Define the assets and liabilities properties
   assets: (number | null)[] = [];
   liabilities: (number | null)[] = [];

   labels: string[] = [
      'netIncome', 'ebit', 'pretaxIncome', 'grossIncome'
   ];

   loading: boolean = true;
   hasData: boolean = false;
   noData: boolean = false;

   constructor(
      private financialService: StockFilterService,
      private cd: ChangeDetectorRef
   ) { }

   ngOnInit() {
      this.setLoadingState = true;
      // Set isAnnual first to ensure proper initialization
      this._isAnnual = true;
      this.processData();
      // Since we're in annual mode, only get annual data
      this.getAnnualData();
   }

   get isAnnual(): boolean {
      return this._isAnnual;
   }

   set isAnnual(value: boolean) {
      this._isAnnual = value;

      // Call specific function based on value
      if (value) {
         this.getAnnualData();
      }
   }

   getAnnualData() {
      this.loading = true;
      this.getNewActualAnnualData(
         this.chartKey,
         'annual',
         this.page_num,
         this.annualData
      ).then(() => {
         // After getting data, check if we have valid data
         this.hasData = this.annualData.length > 0;
         this.noData = !this.hasData || this.annualData.every((item) => {
            return Object.keys(item.data).length === 0 ||
               Object.values(item.data).every(
                  (value) => value === null || value === 0
               );
         });

         this.setLoadingState = false;
         this.loading = false;

         if (this.chart) {
            this.chart.destroy();
         }

         if (!this.noData) {
            setTimeout(() => this.initializeChart(), 0);
         }

         this.cd.detectChanges();
      }).catch(error => {
         console.error("Error loading annual data:", error);
         this.noData = true;
         this.setLoadingState = false;
         this.loading = false;
         this.cd.detectChanges();
      });
   }

   getQuarterlyData() {
      this.loading = true;
      this.getActualYearlyData(
         this.chartKey,
         'annual',
         this.period,
         this.page_num,
         this.quarterlyData,
         this.labels
      );
   }

   async getNewActualAnnualData(
      ticker: string,
      freq: string,
      page_num: number,
      targetArray: any[]
   ) {
      try {
         // Clear existing data before fetching new data
         targetArray.length = 0;

         // Define the priority labels in the desired order
         const topLabels = ['netIncome', 'ebit', 'pretaxIncome', 'grossIncome'];

         let allData: any[] = [];
         let hasMoreData = true;

         // Fetch data until there are no more pages (assuming less than 100 hits means last page)
         while (hasMoreData) {
            const response = await this.financialService.getNewIncomeData(
               ticker,
               freq,
               page_num
            );
            // console.log('Response is', response);

            if (response && response.hits && response.hits.length > 0) {
               allData.push(...response.hits);
               if (response.hits.length < 100) {
                  hasMoreData = false;
               } else {
                  page_num++;
               }
            } else {
               hasMoreData = false;
            }
         }

         // Build a map where the key is the label (only for topLabels) and the value is an object mapping year to value.
         const labelMap = new Map<string, Record<string, number>>();

         // Process each document/hit
         allData.forEach((hit: any) => {
            const doc = hit.document;
            const year = doc.year;
            if (!year) return; // Skip if no year is provided

            // Only process the labels specified in topLabels
            topLabels.forEach((label) => {
               if (
                  doc[label] !== undefined &&
                  doc[label] !== '' &&
                  doc[label] !== null
               ) {
                  const value = parseFloat(doc[label]);
                  if (!isNaN(value)) {
                     if (!labelMap.has(label)) {
                        labelMap.set(label, { [year]: value });
                     } else {
                        const existingData = labelMap.get(label)!;
                        existingData[year] = value;
                        labelMap.set(label, existingData);
                     }
                  }
               }
            });
         });

         // Convert the Map into an array, preserving the order defined in topLabels
         const result = Array.from(labelMap.entries())
            .map(([label, data]) => ({ label, data }))
            .sort(
               (a, b) => topLabels.indexOf(a.label) - topLabels.indexOf(b.label)
            );

         // Filter out any entries that have no data (just in case)
         const filteredResult = result.filter(
            (item) => item.data && Object.keys(item.data).length > 0
         );

         targetArray.push(...filteredResult);
         // console.log('target array from chart annual is :  ', targetArray);

         return filteredResult;
      } catch (error) {
         console.error("Error fetching annual data:", error);
         throw error;
      }
   }

   async getActualYearlyData(
      ticker: string,
      freq: string,
      period: number[],
      page_num: number,
      targetArray: any[],
      labelArray: string[]
   ) {
      this.loading = true;
      this.hasData = false;

      try {
         // Clear existing data before fetching new data
         targetArray.length = 0;

         let allData: any[] = [];

         // Fetch data for each label
         const promises = labelArray.map((label) =>
            this.financialService
               .getStatementChartData(
                  ticker,
                  freq,
                  period,
                  'ic',
                  page_num,
                  'desc',
                  label
               )
               .catch((error) => {
                  console.error(`Error fetching data for ${label}:`, error);
                  return null;
               })
         );

         const responses = await Promise.all(promises);

         // Process responses and collect all data
         responses.forEach((response, index) => {
            if (response?.hits?.length > 0) {
               allData.push(...response.hits);
            }
         });

         const labelMap = new Map<string, Record<string, number>>();

         // Process allData and populate labelMap with validation
         allData.forEach((hit: { document: any }) => {
            const doc = hit.document;
            if (!doc?.label || doc.year == null || doc.value == null) return;

            const { label, year } = doc;
            const value = Number(doc.value);

            if (isNaN(value) || !labelArray.includes(label)) return;

            if (!labelMap.has(label)) {
               labelMap.set(label, {});
            }

            const yearStr = year.toString();
            const labelData = labelMap.get(label)!;
            labelData[yearStr] = (labelData[yearStr] || 0) + value;
         });

         // Convert labelMap to result format with explicit year handling
         const result = Array.from(labelMap.entries()).map(([label, data]) => {
            const yearData: Record<string, number> = {};
            const years = Object.keys(data)
               .map(Number)
               .sort((a, b) => b - a);

            years.slice(0, 5).forEach((year) => {
               yearData[year.toString()] = data[year.toString()];
            });

            return { label, data: yearData };
         });

         targetArray.push(...result.filter(
            (item) => Object.keys(item.data).length > 0
         ));

         this.hasData = targetArray.length > 0;
         this.noData = !this.hasData;

         if (this.chart) {
            this.chart.destroy();
         }

         if (!this.noData) {
            this.initializeChart();
         }

         this.cd.detectChanges();
      } catch (error) {
         console.error("Error in data processing:", error);
         this.hasData = false;
         this.noData = true;
      } finally {
         this.loading = false;
      }
   }

   processData() {
      const requiredYears = ['2020', '2021', '2022', '2023', '2024'];
      const yearPositions: Record<string, number> = {
         '2020': 0,
         '2021': 1,
         '2022': 2,
         '2023': 3,
         '2024': 4,
      };

      this.specialArray.forEach((item) => {
         if (this.labels.includes(item.label)) {
            let tempArray: (number | null)[] = [null, null, null, null, null];

            Object.entries(item.data).forEach(([year, value]) => {
               if (
                  requiredYears.includes(year) &&
                  yearPositions.hasOwnProperty(year)
               ) {
                  tempArray[yearPositions[year]] = value as number;
               }
            });

            const filteredTempArray = tempArray.filter((v) => v !== null);

            if (item.label === 'Net sales') {
               this.assets = filteredTempArray.length
                  ? filteredTempArray
                  : tempArray;
            }

            if (
               [
                  'Gross margin',
                  'Cost of sales',
                  'Provision for income taxes',
                  'Net income',
               ].includes(item.label)
            ) {
               this.liabilities.push(
                  ...(filteredTempArray.length ? filteredTempArray : tempArray)
               );
            }
         }
      });
   }


   ngAfterViewInit() {
      // Ensure chart is initialized after view is ready
      if (this.annualData.length > 0) {
         this.initializeChart();
      }
   }


   @ViewChild('chartCanvas') chartCanvas!: ElementRef<HTMLCanvasElement>;
   private sortQuarterLabels(labels: string[]): string[] {
      // Define the correct order of quarters
      const quarterOrder = ['2020', '2021', '2022', '2023', '2024'];

      // Sort the labels based on the predefined order
      return labels.sort((a, b) => {
         const indexA = quarterOrder.indexOf(a);
         const indexB = quarterOrder.indexOf(b);
         return indexA - indexB;
      });
   }
   // Modify your initializeChart method to handle the data dynamically
   private initializeChart() {
      if (!this.chartCanvas?.nativeElement || !this.annualData?.length) return;

      const ctx = this.chartCanvas.nativeElement.getContext('2d');
      if (!ctx) return;

      // Get all unique years from all datasets
      const allYears = new Set<string>();
      this.annualData.forEach((item) => {
         Object.keys(item.data).forEach((year) => allYears.add(year));
      });

      // Sort years in ascending order
      const labels = Array.from(allYears).sort();

      // Create datasets with values multiplied by 10,00,000
      const datasets = this.annualData.map((item) => {
         return {
            label: this.formatLabel(item.label),
            data: labels.map((year) => {
               const rawValue = item.data[year] || 0;
               return rawValue * 1_000_000; // Multiply before passing to dataset
            }),
            backgroundColor: this.getBackgroundColor(item.label),
            borderRadius: 10,
            categoryPercentage: 0.7, // Spacing between quarters
            barPercentage: 0.8, // Spacing between bars within a quarter
         };
      });

      this.chart = new Chart(ctx, {
         type: 'bar',
         data: { labels, datasets },
         options: {
            responsive: true,
            maintainAspectRatio: false,
            layout: { padding: { top: 30 } },
            plugins: {
               // @ts-ignore
               datalabels: { display: false },
               legend: { display: true, position: 'bottom' },
               tooltip: {
                  callbacks: {
                     label: (tooltipItem: any) => {
                        const value = tooltipItem.raw as number || 0; // Access raw value directly
                        return this.formatYAxisValue(value);
                     },
                  },
               },
            },
            scales: {
               x: { display: true, grid: { display: false } },
               y:
               {
                  display: true,
                  ticks: {
                     callback: (value: any) =>
                        this.formatYAxisValue(value), // Proper formatting
                  },
                  grid: { display: true, color: '#FFFFFF', lineWidth: 3, drawBorder: false },
               },
            },
         },
      });
   }
   formatYAxisValue = (value: number): string => {
      const absValue = Math.abs(value);
      if (absValue >= 1_000_000_000) {
         return (value / 1_000_000_000).toFixed(2) + ' B'; // Billions
      } else if (absValue >= 1_000_000) {
         return (value / 1_000_000).toFixed(2) + ' M'; // Millions
      }
      return value.toString(); // Small numbers
   };

   formatValue = (value: number | undefined): string => {
      if (value === undefined || value === null) return '-';

      value *= 1_000_000; // Multiply first

      const absValue = Math.abs(value);
      const formattedValue = (val: number) =>
         val.toFixed(2).replace(/\.?0+$/, ''); // Ensure 2 decimal places

      let result: string;
      if (absValue >= 1_000_000_000) {
         result = formattedValue(value / 1_000_000_000) + ' B'; // Billions
      } else if (absValue >= 1_000_000) {
         result = formattedValue(value / 1_000_000) + ' M'; // Millions
      } else {
         result = formattedValue(value); // Small numbers
      }

      return value < 0 ? `(${result})` : result; // Show negative values in parentheses
   };

   // Add a method to format the label for display
   private formatLabel(label: string): string {
      // Convert camelCase to Title Case with spaces
      switch (label) {
         case 'netIncome':
            return 'Net Income';
         case 'ebit':
            return 'EBIT';
         case 'pretaxIncome':
            return 'Income before Tax';
         case 'grossIncome':
            return 'Gross Margin';
         default:
            return label
               .replace(/([A-Z])/g, ' $1')
               .replace(/^./, (str) => str.toUpperCase());
      }
   }

   // Updated method to get color based on label
   private getBackgroundColor(label: string): string {
      switch (label) {
         case 'netIncome':
            return 'rgba(255, 99, 132, 1)'; // Red
         case 'pretaxIncome':
            return 'rgba(54, 162, 235, 1)'; // Blue
         case 'ebit':
            return 'rgba(255, 206, 86, 1)'; // Yellow
         case 'grossIncome':
            return 'rgba(75, 192, 192, 1)'; // Teal

         default:
            return '#9C27B0'; // Purple as fallback
      }
   }

   // Add a controller method to fetch and update the chart
   async updateChart(ticker: string, freq: string = 'annual') {
      this.setLoadingState = true;

      try {
         // Clear previous data
         this.annualData = [];

         // Fetch new data
         await this.getNewActualAnnualData(ticker, freq, 1, this.annualData);

         // Destroy previous chart if it exists
         if (this.chart) {
            this.chart.destroy();
         }

         // Initialize chart with new data
         this.initializeChart();
      } catch (error) {
         console.error('Error updating chart:', error);
      } finally {
         this.setLoadingState = false;
      }
   }

   // Helper method to format values for display (e.g., in millions or billions)
}
